import { css } from "@emotion/core"
import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/core"
import React from "react"
import { Button } from "../Buttons"
import Body from "../typography/Body"
import Heading from "../typography/Heading"
import { bp } from "../../utils/MediaQueries"
import { getExtendedWarrantyContent } from "../../utils/extendedWarranty"
import "../BikeProductPage/style.css"

const DEFAULT_SUBTITLE = "Add extended warranty for your bike?"

const copyStyles = css`
  &.extended-warranty-modal-copy h3 {
    text-align: center;
    font-size: 1.0625rem;
    font-weight: 700;
    margin-top: 0;
    margin-bottom: 0.75rem;
    letter-spacing: 0.02em;
    color: inherit;
  }
  &.extended-warranty-modal-copy h4 {
    font-weight: 700;
    margin-top: 1.25rem;
    margin-bottom: 0.5rem;
    font-size: 0.95rem;
  }
  &.extended-warranty-modal-copy h5 {
    font-weight: 600;
    margin-top: 0.875rem;
    margin-bottom: 0.375rem;
    font-size: 0.875rem;
  }
  &.extended-warranty-modal-copy ul {
    list-style: disc;
    list-style-position: outside;
    padding-left: 1.25rem;
    margin: 0 0 0.75rem 0;
  }
  &.extended-warranty-modal-copy li {
    margin-bottom: 0.4rem;
    line-height: 1.45;
    display: list-item;
  }
  &.extended-warranty-modal-copy p {
    margin-bottom: 0.75rem;
    line-height: 1.55;
  }
`

/** Shield + check badge (brand teal + white ring). */
const ExtendedWarrantyBadgeIcon = ({ size = "5rem" }: { size?: string }) => (
  <svg
    viewBox="0 0 120 120"
    width={size}
    height={size}
    aria-hidden="true"
    style={{ display: "block" }}
  >
    <circle cx="60" cy="60" r="58" fill="white" />
    <circle cx="60" cy="60" r="48" fill="#00C4D1" />
    <path
      d="M60 32 L86 42 L86 64 Q86 82 60 98 Q34 82 34 64 L34 42 Z"
      fill="none"
      stroke="white"
      strokeWidth="4"
      strokeLinejoin="round"
    />
    <path
      d="M46 60 L56 72 L78 50"
      fill="none"
      stroke="white"
      strokeWidth="5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export type ExtendedWarrantyPromptModalProps = {
  isOpen: boolean
  onClose: () => void
  onYes: () => void
  onNo: () => void
  isLoading?: boolean
  /** Small line above the main title (e.g. last-chance messaging). */
  eyebrow?: string
  /** Optional main heading (uppercase styling when set). */
  title?: string
  subtitle?: string
  htmlBody?: string
  yesLabel?: string
  noLabel?: string
}

/**
 * Extended warranty prompt — overlapping badge, padded panel, Yes / No.
 */
export default function ExtendedWarrantyPromptModal({
  isOpen,
  onClose,
  onYes,
  onNo,
  isLoading = false,
  eyebrow,
  title,
  subtitle = DEFAULT_SUBTITLE,
  htmlBody = getExtendedWarrantyContent(),
  yesLabel = "Yes",
  noLabel = "No",
}: ExtendedWarrantyPromptModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay
        bg="rgba(15, 23, 42, 0.55)"
        style={{ backdropFilter: "blur(6px)" } as React.CSSProperties}
      />
      <ModalContent
        position="relative"
        overflow="visible"
        width={["80%", null, null, null, "40vw"]}
        maxWidth={["400px", null, null, null, "37rem"]}
        maxH={[null, null, null, null, "80vh"]}
        minH="18.75rem"
        pt={bp("3.25rem", "3.625rem")}
        px={["1.5rem", null, null, null, "3.625rem"]}
        pb={["0.75rem", null, null, null, "1rem"]}
        mx="0.5rem"
        textAlign="center"
        borderRadius="0.5rem"
      >
        <Box
          position="absolute"
          left="50%"
          top={0}
          transform="translate(-50%, -50%)"
          zIndex={2}
          style={{ filter: "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.12))" }}
        >
          <ExtendedWarrantyBadgeIcon size="5.25rem" />
        </Box>

        <ModalCloseButton
          position="absolute"
          right="0.75rem"
          top="0.75rem"
          zIndex={3}
          fontSize="1rem"
          color="dawn"
        />

        <ModalHeader
          pt="0.25rem"
          pb="0"
          px="0"
          textAlign="center"
          border="none"
        >
          {eyebrow ? (
            <Body
              as="p"
              display="block"
              color="night"
              fontWeight="bold"
              letterSpacing="0.04em"
              mb={title ? "0.5rem" : "0.25rem"}
              textTransform="uppercase"
              fontSize={bp("0.9375rem", "1.125rem")}
              lineHeight="1.35"
            >
              {eyebrow}
            </Body>
          ) : null}
          {title ? (
            <Heading
              as="h2"
              size="4"
              fontWeight="bold"
              letterSpacing="0.12em"
              textTransform="uppercase"
              color="night"
              lineHeight="1.25"
            >
              {title}
            </Heading>
          ) : null}
        </ModalHeader>

        <ModalBody
          pt="0.5rem"
          pb="0.5rem"
          px="0"
          maxH={["50vh", null, null, null, "calc(80vh - 14rem)"]}
          overflowY="auto"
        >
          <Body
            size="lg"
            color="dusk"
            textAlign="center"
            mb={bp("0.625rem", "0.75rem")}
            lineHeight="1.5"
            fontSize={bp("1.125rem", "1.25rem")}
            fontWeight="medium"
          >
            {subtitle}
          </Body>
          <Box
            className="extended-warranty extended-warranty-modal-copy"
            css={copyStyles}
            fontSize="0.875rem"
            lineHeight="1.55"
            color="dusk"
            textAlign="left"
            dangerouslySetInnerHTML={{ __html: htmlBody }}
          />
        </ModalBody>

        <ModalFooter
          justifyContent="center"
          flexWrap="wrap"
          pt="0.75rem"
          pb="0"
          px="0"
          border="none"
        >
          <Button
            theme="primary"
            mr="1rem"
            mb={bp("0.75rem", "0")}
            minW="6.5rem"
            isDisabled={isLoading}
            onClick={onYes}
          >
            {yesLabel}
          </Button>
          <Button
            theme="secondary"
            mb={bp("0.75rem", "0")}
            minW="6.5rem"
            isDisabled={isLoading}
            onClick={onNo}
          >
            {noLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
